import { FC, ReactNode } from 'react';

import AccentHeadline from './AccentHeadline';
import type { SanityAccentHeadline } from '@lib/sanity/types';

type StepHeaderProps = {
  eyebrow?: string;
  /** Plain string or composed nodes (e.g. a headline with a highlighted metric). */
  heading?: ReactNode;
  /** Portable-text accent heading; takes precedence over `heading` when set. */
  accentHeading?: SanityAccentHeadline | null;
  subtext?: string;
  /** Forwarded to the heading element (e.g. for test hooks). */
  headingDataCy?: string;
  className?: string;
};

// Typography comes from the global .cms-* classes (styles/globals.css) shared by
// every Sanity-driven page; only the colours are set here.
const HEADING_CLASSES = 'cms-heading text-neutral-900';

/**
 * The shared step-funnel header used across every funnel page (GYTR,
 * readiness, paywall): an accent eyebrow, the bold centred heading and an
 * optional subtext. The heading can be a plain string, composed nodes, or a
 * portable-text accent value (`accentHeading`) so individual words can be
 * tinted. Keeping all headers here avoids style drift between pages.
 */
const StepHeader: FC<StepHeaderProps> = ({
  eyebrow,
  heading,
  accentHeading,
  subtext,
  headingDataCy,
  className
}) => {
  const hasAccent = Boolean(accentHeading && accentHeading.length > 0);
  const hasHeading =
    hasAccent || (heading !== undefined && heading !== null && heading !== '');

  if (!hasHeading) return null;

  return (
    <div className={`text-center ${className ?? ''}`}>
      {eyebrow && (
        <p className="mb-2 cms-eyebrow text-primary-500">{eyebrow}</p>
      )}
      {hasAccent ? (
        <AccentHeadline
          value={accentHeading}
          className={HEADING_CLASSES}
          dataCy={headingDataCy}
        />
      ) : (
        <h1 className={HEADING_CLASSES} data-cy={headingDataCy}>
          {heading}
        </h1>
      )}
      {subtext && (
        <p className="mt-3 cms-subtext text-neutral-500">{subtext}</p>
      )}
    </div>
  );
};

export default StepHeader;
